import { CheckCircle, X, Printer } from "lucide-react"
import { useEffect, useState, useMemo } from "react"
import { useTheme } from "../../contexts/ThemeContext"
import { navigateTo } from "../../utils/navigation"
import { useSelector, useDispatch } from "react-redux"
import { fetchOrderDetails, selectPaymentOrder, selectPaymentLoading, selectPaymentError, clearPaymentState, type OrderDetails } from "../payment/paymentSlice"
import { clearCart } from "../cart/cartSlice"
import { logout } from "../auth/authSlice"

// Extended OrderDetails interface to include additional discount fields
interface ExtendedOrderDetails extends OrderDetails {
  coupon_code?: string
  promo_code?: string
  coupon_discount?: number
  promo_discount?: number
}
import { clearSuccessPageData, clearAllDataAfterPayment } from "../../utils/cartCleaner"
import type { AppDispatch } from "../../store"
import { colors } from "../../styles/colors"
import { configureArabicFont, addArabicText, createStyledHeader, createStyledBox, formatCurrency, formatDate } from "../../utils/pdfUtils"
import { useTranslation } from "../../hooks/useTranslation"
import { useCountryConfig } from "../../hooks/useCountryConfig"
import { getCurrencyTextLabel } from "../../utils/currencyUtils"
import CheckoutOfferCard from "../../components/offers/CheckoutOfferCard"
import {
  fromOrderPromotion,
  offerSummaryRows,
  receiptPromotionLabel,
} from "../../features/offers/offerBenefits"

// Theme colors for consistent styling
const themeColors = {
  primaryColor: colors.primary.main,
  secondaryColor: colors.secondary.main,
  successBg: colors.success.bg,
  successGradient: "from-[#E6F4EA] to-[#C8E6C9]",
  cardBg: colors.background.card,
  textPrimary: colors.neutral.gray900,
  textSecondary: colors.neutral.gray600,
  textMuted: colors.neutral.gray500,
  borderColor: colors.neutral.gray300,
  buttonGray: colors.neutral.gray600,
}

export default function SuccessScreen() {
  const { primaryColor } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const { t, isRTL } = useTranslation()
  const countryConfig = useCountryConfig()
  const currencyLabel = getCurrencyTextLabel(
    countryConfig,
    t('currency.egyptSymbol'),
    t('common.currencySar')
  )

  const orderDetails = useSelector(selectPaymentOrder)
  const loading = useSelector(selectPaymentLoading)
  const error = useSelector(selectPaymentError)
  const language = useSelector((state: any) => state.language.language || "ar")

  const [orderId, setOrderId] = useState<string | null>(null)
  const [localOrderDetails, setLocalOrderDetails] = useState<ExtendedOrderDetails | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  const [showNoData, setShowNoData] = useState(false)
  const maxRetries = 3 // تقليل عدد المحاولات
  const retryDelay = 1000 // تقليل التأخير إلى ثانية واحدة

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlOrderId = urlParams.get("orderId")
    const sessionOrderId = sessionStorage.getItem("currentOrderId")
    
    // أولوية للـ orderId من URL (من Paymob redirect)
    const finalOrderId = urlOrderId || sessionOrderId
    setOrderId(finalOrderId)
    
    // تصحيح: طباعة معلومات التصحيح
    console.log('🔍 SuccessScreen Debug Info:', {
      urlOrderId,
      sessionOrderId,
      finalOrderId,
      currentOrderData: sessionStorage.getItem("currentOrderData"),
      orderData: sessionStorage.getItem("orderData"),
      currentOrderCartData: sessionStorage.getItem("currentOrderCartData"),
      orderCartData: sessionStorage.getItem("orderCartData"),
      allSessionStorageKeys: Object.keys(sessionStorage),
      urlSearchParams: window.location.search
    })
    
    // إذا وصلنا مباشرة إلى الصفحة مع orderId ولكن بدون بيانات محفوظة
    // قد يكون هذا يحدث عند التوجيه المباشر من Paymob
    if (finalOrderId && !sessionStorage.getItem("currentOrderData") && !sessionStorage.getItem("orderData")) {
      // إنشاء بيانات أساسية للطلب
      const basicOrderData = {
        id: finalOrderId,
        order_number: finalOrderId,
        payment_status: 'paid',
        status: 'confirmed',
        created_at: new Date().toISOString(),
        customer_phone: t('successScreen.notAvailable'),
        payment_method: "online",
        user: {
          name: t('successScreen.notAvailable'),
          phone: t('successScreen.notAvailable')
        },
        branch: t('successScreen.notAvailable'),
        items: [],
        total_price: 0,
        subtotal: 0,
        tax_value: 0,
        discount: 0
      }
      
      // حفظ البيانات الأساسية
      sessionStorage.setItem("currentOrderData", JSON.stringify(basicOrderData))
      console.log('✅ Basic order data created:', basicOrderData)
      
      // استدعاء API فوري إضافي هنا أيضاً
      const immediateToken = localStorage.getItem("auth_token") || 
                            sessionStorage.getItem("auth_token") ||
                            sessionStorage.getItem("orderToken") || 
                            sessionStorage.getItem("currentOrderToken") ||
                            sessionStorage.getItem("authToken")
      
      if (immediateToken && finalOrderId) {
        console.log('🚀 Making EXTRA immediate API call from useEffect...')
        dispatch(fetchOrderDetails({
          orderId: finalOrderId,
          token: immediateToken,
          lang: language,
        }))
        .then((result) => {
          console.log('✅ Extra API call result:', result)
        })
        .catch((error) => {
          console.error('❌ Extra API call failed:', error)
        })
      }
    }
    
  }, [dispatch, language, t])

  const actualOrderId = orderId

  // دالة retry محسنة وسريعة لـ fetchOrderDetails
  const retryFetchOrderDetails = async (orderId: string, token: string, lang: string) => {
    if (retryCount >= maxRetries) {
      setIsRetrying(false)
      console.log('❌ Max retries reached, stopping')
      return
    }

    setIsRetrying(true)
    setRetryCount(prev => prev + 1)
    
    console.log(`🔄 Retry attempt ${retryCount + 1}/${maxRetries} for order:`, orderId)
    
    try {
      const result = await dispatch(fetchOrderDetails({
        orderId: orderId,
        token: token,
        lang: lang,
      })).unwrap()
      
      setIsRetrying(false)
      console.log('✅ Retry successful! Order details loaded:', result)
      return result
    } catch (error) {
      console.error(`❌ Retry ${retryCount + 1} failed:`, error)
      
      if (retryCount + 1 < maxRetries) {
        // تأخير قصير جداً للمحاولة التالية
        const delay = 300 * (retryCount + 1) // 300ms, 600ms, 900ms
        console.log(`⏱️ Waiting ${delay}ms before next retry...`)
        setTimeout(() => {
          retryFetchOrderDetails(orderId, token, lang)
        }, delay)
      } else {
        setIsRetrying(false)
        console.log('❌ All retry attempts failed')
      }
    }
  }



  // دالة لعرض السعر بشكل طبيعي مع الترجمة
  const sarWithSeparatedFractions = (n: number) => {
    const price = n || 0
    // التحقق من أن السعر رقم صالح
    const safePrice = isNaN(price) ? 0 : price
    const currency = currencyLabel
    return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(safePrice)} ${currency}`
  }



  // Helper function to calculate total item price including additionals
  const calculateItemTotalPrice = (item: any): number => {
    try {
      // Base price from item - التحقق من صحة السعر
      const basePrice = parseFloat(item.price) || 0
      
      // Calculate additionals price
      let additionalsPrice = 0
      if (item.meals?.[0]?.additionals?.length > 0) {
        additionalsPrice = item.meals[0].additionals.reduce((sum: number, addon: any) => {
          const addonPrice = parseFloat(addon.price) || 0
          const addonQuantity = addon.quantity || 1
          return sum + (addonPrice * addonQuantity)
        }, 0)
      }
      
      // التحقق من أن الكمية صالحة - معالجة null و undefined
      const itemQuantity = item.quantity === null || item.quantity === undefined ? 1 : (item.quantity || 1)
      
      // Total price per unit (base + additionals) * quantity
      const totalPrice = (basePrice + additionalsPrice) * itemQuantity
      
      // التحقق من أن النتيجة رقم صالح
      return isNaN(totalPrice) ? 0 : totalPrice
    } catch (error) {
      console.error('Error calculating item total price:', error)
      // محاولة حساب بسيط كبديل
      const fallbackPrice = parseFloat(item.price) || 0
      const fallbackQuantity = item.quantity === null || item.quantity === undefined ? 1 : (item.quantity || 1)
      const fallbackTotal = fallbackPrice * fallbackQuantity
      return isNaN(fallbackTotal) ? 0 : fallbackTotal
    }
  }

  const fmtDate = (s: string) => {
    try {
      // التحقق من أن التاريخ صالح
      if (!s || typeof s !== 'string') {
        const arLocale = countryConfig.country.code ? `ar-${countryConfig.country.code}` : 'ar-SA'
        return new Date().toLocaleDateString(language === 'ar' ? arLocale : 'en-US')
      }
      
      const date = new Date(s)
      if (isNaN(date.getTime())) {
        const arLocale = countryConfig.country.code ? `ar-${countryConfig.country.code}` : 'ar-SA'
        return new Date().toLocaleDateString(language === 'ar' ? arLocale : 'en-US')
      }
      
      const arLocale = countryConfig.country.code ? `ar-${countryConfig.country.code}` : 'ar-SA'
      return new Intl.DateTimeFormat(language === 'ar' ? arLocale : 'en-US', {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(date)
    } catch {
      const arLocale = countryConfig.country.code ? `ar-${countryConfig.country.code}` : 'ar-SA'
      return new Date().toLocaleDateString(language === 'ar' ? arLocale : 'en-US')
    }
  }

  // استدعاء API فوري ومباشر بدون أي تأخير
  useEffect(() => {
    if (actualOrderId) {
      console.log('🚀 Starting immediate API call for order:', actualOrderId)
      
      // جلب البيانات من sessionStorage للعرض الفوري
      const storedOrderDataKeys = ["currentOrderData", "orderData"]
      const storedCartDataKeys = ["currentOrderCartData", "orderCartData"]
      
      let storedOrderData = null
      let storedCartData = null
      
      // البحث عن بيانات محفوظة للعرض الفوري
      for (const key of storedOrderDataKeys) {
        const data = sessionStorage.getItem(key)
        if (data) {
          storedOrderData = data
          break
        }
      }
      
      for (const key of storedCartDataKeys) {
        const data = sessionStorage.getItem(key)
        if (data) {
          storedCartData = data
          break
        }
      }
      
      // عرض البيانات المحفوظة فوراً إذا كانت متوفرة
      if (storedOrderData) {
        try {
          const parsedOrderData = JSON.parse(storedOrderData)
          setLocalOrderDetails(parsedOrderData)
          setShowNoData(false)
          console.log('📦 Loaded stored order data for immediate display')
        } catch (error) {
          console.error('Error parsing stored order data:', error)
        }
      }

      // استدعاء API فوراً ومباشرة - لا تأخير على الإطلاق
      const makeImmediateAPICall = () => {
        const getAuthToken = (): string | null => {
          return localStorage.getItem("auth_token") || 
                 sessionStorage.getItem("auth_token") ||
                 sessionStorage.getItem("orderToken") || 
                 sessionStorage.getItem("currentOrderToken") ||
                 sessionStorage.getItem("authToken") ||
                 null
        }

        const token = getAuthToken()
        
    
        if (token) {
          // استدعاء فوري بدون أي تأخير
          dispatch(fetchOrderDetails({
            orderId: actualOrderId,
            token: token,
            lang: language,
          }))
          .then((result) => {
            if (result.type === 'payment/fetchOrderDetails/fulfilled') {
              console.log('✅ API call successful! Order details loaded:', result.payload)
            } else {
              console.log('⚠️ API call completed but not fulfilled:', result)
            }
          })
          .catch((error) => {
            console.error('❌ API call failed:', error)
            // retry فوري بدون تأخير إذا فشل
            if (retryCount < maxRetries) {
              console.log('🔄 Retrying API call immediately...')
              setTimeout(() => {
                retryFetchOrderDetails(actualOrderId, token, language)
              }, 500) // تأخير قصير جداً - نصف ثانية فقط
            }
          })
        } else {
          console.error('❌ No authentication token found - cannot make API call')
          // حتى بدون token، حاول استدعاء API (قد يعمل مع بعض الـ endpoints العامة)
          dispatch(fetchOrderDetails({
            orderId: actualOrderId,
            token: '', // token فارغ
            lang: language,
          }))
          .catch((error) => {
            console.error('❌ API call without token failed:', error)
          })
        }
      }

      // تنفيذ API call فوراً
      makeImmediateAPICall()
    }
  }, [actualOrderId, dispatch, language, retryCount])

  useEffect(() => {
    if (orderDetails) {
      if (orderDetails.id || orderDetails.order_number) {
        setLocalOrderDetails(orderDetails)
        // احفظ البيانات الجديدة في sessionStorage للاستخدام المستقبلي
        sessionStorage.setItem("currentOrderData", JSON.stringify(orderDetails))
        
        // توقف عن retry إذا نجحنا في الحصول على البيانات
        setIsRetrying(false)
        setRetryCount(0)
      }
    }
  }, [orderDetails])

  // تنظيف البيانات عند تغيير orderId
  useEffect(() => {
    if (actualOrderId) {
      // امسح البيانات القديمة من Redux store
      dispatch(clearPaymentState())
      // امسح البيانات المحلية
      setLocalOrderDetails(null)
    }
  }, [actualOrderId, dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearPaymentState())
    }
  }, [dispatch])

  // Function to clear all data after successful order
  const clearAllDataAfterOrder = () => {
    try {
      // Clear cart from Redux store
      dispatch(clearCart())
      
      // Clear auth token
      dispatch(logout())
      
      // Clear all stored data using the utility function
      clearAllDataAfterPayment()
      
      // Clear payment state
      dispatch(clearPaymentState())
      
      console.log('✅ All data cleared after successful order')
    } catch (error) {
      console.error('❌ Error clearing data after order:', error)
    }
  }

  const handleClose = () => {
    clearAllDataAfterOrder()
    navigateTo("home")
  }

  // دالة لعرض السعر بشكل طبيعي في PDF
  const formatPriceForPDF = (price: number): string => {
    const safePrice = price || 0
    // التحقق من أن السعر رقم صالح
    const validPrice = isNaN(safePrice) ? 0 : safePrice
    const currency = currencyLabel
    return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(validPrice)} ${currency}`
  }

  const handleDownloadInvoice = async () => {
    if (!finalDisplayOrderDetails || !finalOrderCalculations) {
      console.error('Missing order details or calculations for invoice generation')
      alert(t('successScreen.invoiceErrorMessage'))
      return
    }
    
    try {
      // Import required libraries dynamically
      const { jsPDF } = await import('jspdf')
      const html2canvas = (await import('html2canvas')).default
      
      // Create a temporary container for the invoice
      const invoiceContainer = document.createElement('div')
      invoiceContainer.style.position = 'absolute'
      invoiceContainer.style.left = '-9999px'
      invoiceContainer.style.top = '-9999px'
      invoiceContainer.style.width = '800px'
      invoiceContainer.style.backgroundColor = 'white'
      invoiceContainer.style.padding = '20px'
      invoiceContainer.style.fontFamily = 'Arial, sans-serif'
      invoiceContainer.style.direction = isRTL ? 'rtl' : 'ltr'
      invoiceContainer.style.textAlign = isRTL ? 'right' : 'left'
      
      // Get currency based on Settings country config
      const currency = currencyLabel
      const dir = isRTL ? 'rtl' : 'ltr'
      const lang = isRTL ? 'ar' : 'en'
      
      // Generate HTML content for the invoice with SuccessScreen-style design
      const invoiceHTML = `
        <!DOCTYPE html>
        <html dir="${dir}" lang="${lang}">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${t('successScreen.invoiceTitle')}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Cairo', 'Noto Kufi Arabic', Arial, sans-serif;
              direction: ${dir};
              background: #F9FAFB;
              color: #374151;
              line-height: 1.6;
              min-height: 100vh;
            }
            
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              border-radius: 16px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
              overflow: hidden;
            }
            
            .success-header {
              background: #F9FAFB;
              padding: 30px 20px;
              text-align: center;
              position: relative;
            }
            
            .success-icon {
              width: 80px;
              height: 80px;
              margin: 0 auto 20px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            .success-icon img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
            
            .success-title {
              color: #374151;
              font-size: 28px;
              font-weight: 700;
              margin: 0;
            }
            
            .content {
              padding: 30px;
            }
            
            .section {
              margin-bottom: 25px;
            }
            
            .section-title {
              font-size: 18px;
              font-weight: 700;
              color: #6B7280;
              margin-bottom: 15px;
              padding-bottom: 8px;
              border-bottom: 2px solid #E5E7EB;
            }
            
            .info-card {
              background: #F8FAFC;
              border-radius: 12px;
              padding: 20px;
              border: 1px solid #E5E7EB;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 8px 0;
              border-bottom: 1px dashed #D1D5DB;
            }
            
            .info-row:last-child {
              border-bottom: none;
            }
            
            .info-label {
              font-weight: 600;
              color: #374151;
            }
            
            .info-value {
              color: #6B7280;
              font-weight: 500;
            }
            
            .order-number-box {
              background: white;
              border: 2px dashed #6B7280;
              border-radius: 12px;
              padding: 20px;
              text-align: center;
              margin-bottom: 20px;
            }
            
            .order-number-label {
              font-size: 14px;
              color: #6B7280;
              margin-bottom: 8px;
            }
            
            .order-number-value {
              font-size: 32px;
              font-weight: 700;
              color: #6B7280;
            }
            
            .products-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              background: white;
              border-radius: 12px;
              overflow: hidden;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            }
            
            .products-table th {
              background: transparent;
              color: #000000;
              padding: 15px 12px;
              text-align: center;
              font-weight: 600;
              font-size: 14px;
              border-bottom: 1px solid #E5E7EB;
            }
            
            .products-table td {
              padding: 15px 12px;
              border-bottom: 1px solid #F3F4F6;
              text-align: center;
            }
            
            .products-table tr:nth-child(even) {
              background: #F8FAFC;
            }
            
            .product-name {
              text-align: ${isRTL ? 'right' : 'left'};
              font-weight: 600;
              color: #374151;
            }
            
            .price {
              text-align: ${isRTL ? 'left' : 'right'};
              font-weight: 600;
              color: #374151;
            }
            
            .product-additionals {
              font-size: 12px;
              color: #6B7280;
              margin-top: 4px;
              font-weight: 400;
            }
            
            .summary-card {
              background: #F8FAFC;
              border-radius: 12px;
              padding: 20px;
              margin-bottom: 20px;
            }
            
            .summary-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 10px 0;
              border-bottom: 1px dashed #D1D5DB;
            }
            
            .summary-row:last-child {
              border-bottom: none;
            }
            
            .summary-label {
              font-weight: 600;
              color: #374151;
            }
            
            .summary-value {
              font-weight: 600;
              color: #6B7280;
            }
            
            .grand-total {
              background: transparent;
              color: #000000;
              padding: 20px;
              border-radius: 12px;
              text-align: center;
              font-size: 20px;
              font-weight: 700;
              margin-bottom: 20px;
              border: 1px solid #E5E7EB;
            }
            
            .footer-note {
              background: #F8FAFC;
              border-radius: 12px;
              padding: 20px;
              text-align: center;
              font-size: 14px;
              color: #6B7280;
              border: 1px solid #E5E7EB;
            }
            
            .thank-you {
              font-size: 16px;
              font-weight: 600;
              color: #6B7280;
              margin-bottom: 10px;
            }
            
            .discount {
              color: #6B7280;
            }
            
            @media print {
              body {
                background: white;
              }
              .invoice-container {
                box-shadow: none;
                border-radius: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <!-- Success Header -->
            <div class="success-header">
              <div class="success-icon">
                <img src="/done.png" alt="Success" />
              </div>
              <h1 class="success-title">${t('successScreen.orderSentSuccessfully')}</h1>
            </div>
            
            <div class="content">
              <!-- Order Information -->
              <div class="section">
                <div class="order-number-box">
                  <div class="order-number-label">${t('successScreen.orderNumber')}</div>
                  <div class="order-number-value">${finalDisplayOrderDetails.order_number || finalDisplayOrderDetails.id || t('successScreen.notAvailable')}</div>
                </div>
                
                <div class="info-card">
                  <div class="info-row">
                    <span class="info-label">${t('successScreen.dateAndTime')}</span>
                    <span class="info-value">${formatDate(finalDisplayOrderDetails.created_at || new Date().toISOString(), language)}</span>
                  </div>
                  ${finalDisplayOrderDetails.table ? `
                    <div class="info-row">
                      <span class="info-label">${t('successScreen.tableNumber')}</span>
                      <span class="info-value">${finalDisplayOrderDetails.table.name || finalDisplayOrderDetails.table || t('successScreen.notAvailable')}</span>
                    </div>
                  ` : ''}
                </div>
              </div>
              
              <!-- Customer Information -->
              <div class="section">
                <div class="info-card">
                  <div class="info-row">
                    <span class="info-label">${t('successScreen.name')}</span>
                    <span class="info-value">${finalDisplayOrderDetails.user?.name || t('successScreen.notAvailable')}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">${t('successScreen.phoneNumber')}</span>
                    <span class="info-value">${finalDisplayOrderDetails.customer_phone || finalDisplayOrderDetails.user?.phone || t('successScreen.notAvailable')}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">${t('successScreen.branch')}</span>
                    <span class="info-value">${typeof finalDisplayOrderDetails.branch === 'string' ? finalDisplayOrderDetails.branch : finalDisplayOrderDetails.branch?.name || t('successScreen.notAvailable')}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">${t('successScreen.paymentMethod')}</span>
                    <span class="info-value">${finalDisplayOrderDetails.payment_method || t('successScreen.online')}</span>
                  </div>
                </div>
              </div>
              
              <!-- Products Table -->
              <div class="section">
                <table class="products-table">
                  <thead>
                    <tr>
                      <th>${t('successScreen.product')}</th>
                      <th>${t('successScreen.quantity')}</th>
                      <th>${t('successScreen.price')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${finalDisplayOrderDetails.items?.map((item: any) => `
                      <tr>
                        <td class="product-name">
                          <div>${item.meals?.[0]?.name || item.name || t('successScreen.product')}</div>
                          ${item.meals?.[0]?.additionals?.length > 0 ? `
                            <div class="product-additionals">- ${item.meals[0].additionals.map((addon: any) => addon.name).join(' - ')}</div>
                          ` : ''}
                        </td>
                        <td>${item.quantity === null || item.quantity === undefined ? 1 : (item.quantity || 1)}</td>
                        <td class="price">${(calculateItemTotalPrice(item) || 0).toFixed(2)} ${currency}</td>
                      </tr>
                    `).join('') || ''}
                  </tbody>
                </table>
              </div>
              
              <!-- Summary Section -->
              <div class="section">
                <div class="summary-card">
                  <div class="summary-row">
                    <span class="summary-label">${t('successScreen.subtotal')}</span>
                    <span class="summary-value">${(finalOrderCalculations.subtotal || 0).toFixed(2)} ${currency}</span>
                  </div>
                  ${(finalOrderCalculations.tablePrice || 0) > 0 ? `
                    <div class="summary-row">
                      <span class="summary-label">${t('successScreen.tablePrice')}</span>
                      <span class="summary-value">${(finalOrderCalculations.tablePrice || 0).toFixed(2)} ${currency}</span>
                    </div>
                  ` : ''}
                  ${(finalOrderCalculations.deliveryPrice || 0) > 0 ? `
                    <div class="summary-row">
                      <span class="summary-label">${t('successScreen.deliveryFees')}</span>
                      <span class="summary-value">${(finalOrderCalculations.deliveryPrice || 0).toFixed(2)} ${currency}</span>
                    </div>
                  ` : ''}
                  ${promotionSummaryRows.length > 0
                    ? promotionSummaryRows
                        .map(
                          (row) => `
                    <div class="summary-row">
                      <span class="summary-label ${row.showMinus ? 'discount' : ''}">${receiptPromotionLabel(row, t)}</span>
                      <span class="summary-value ${row.showMinus ? 'discount' : ''}">${row.showMinus ? '-' : ''}${(row.amount ?? 0).toFixed(2)} ${currency}</span>
                    </div>`,
                        )
                        .join('')
                    : ''}
                  ${!hasPromotionDiscountRow && (finalOrderCalculations.discount || 0) > 0.01 ? `
                    <div class="summary-row">
                      <span class="summary-label discount">${t('successScreen.couponDiscount')}</span>
                      <span class="summary-value discount">-${(finalOrderCalculations.discount || 0).toFixed(2)} ${currency}</span>
                    </div>
                  ` : ''}
                  <div class="summary-row">
                    <span class="summary-label">${t('successScreen.taxWithPercentage')}</span>
                    <span class="summary-value">${(finalOrderCalculations.taxValue || 0).toFixed(2)} ${currency}</span>
                  </div>
                </div>
                
                <div class="grand-total">
                  ${t('successScreen.grandTotalLabelShort')} ${(finalOrderCalculations.totalPrice || 0).toFixed(2)} ${currency}
                </div>
              </div>
              
              <!-- Footer Message -->
              <div class="footer-note">
                <div class="thank-you">${t('successScreen.thankYouMessage')}</div>
              
              </div>
            </div>
          </div>
        </body>
        </html>
      `
      
      invoiceContainer.innerHTML = invoiceHTML
      document.body.appendChild(invoiceContainer)
      
      // Wait a bit for fonts to load
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Convert HTML to canvas
      const canvas = await html2canvas(invoiceContainer, {
        useCORS: true,
        background: '#ffffff',
        logging: false,
        allowTaint: true,
        width: 800,
        height: invoiceContainer.scrollHeight
      })
      
      // Remove temporary container
      document.body.removeChild(invoiceContainer)
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png', 1.0)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      // Calculate dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = 0
      
      // Add image to PDF
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
      
      // Generate filename
       const orderNumber = finalDisplayOrderDetails.order_number || finalDisplayOrderDetails.id || 'unknown'
      const fileName = `invoice_${orderNumber}.pdf`
      
      // Download PDF
      pdf.save(fileName)
      
      console.log('✅ Invoice PDF generated and downloaded successfully:', fileName)
      
    } catch (error) {
      console.error('❌ Error generating invoice PDF:', error)
      
      // More specific error handling
      if (error instanceof Error) {
        if (error.message.includes('html2canvas')) {
          alert(t('successScreen.invoiceConversionError'))
        } else if (error.message.includes('jsPDF')) {
          alert(t('successScreen.invoiceCreationError'))
        } else {
          alert(t('successScreen.invoiceUnexpectedError'))
        }
      } else {
        alert(t('successScreen.invoiceGeneralError'))
      }
    }
  }

  const handleBackToHome = () => {
    clearAllDataAfterOrder()
    navigateTo("home")
  }

  const displayOrderDetails = localOrderDetails || orderDetails
  
  // Memoized calculations for better performance
  const orderCalculations = useMemo(() => {
    if (!displayOrderDetails) return null
    
    const subtotal = displayOrderDetails.subtotal || displayOrderDetails.price_before_discount || displayOrderDetails.price || 0
    const taxValue = displayOrderDetails.tax_value || displayOrderDetails.taxValue || 0
    const totalPrice = displayOrderDetails.total_price || displayOrderDetails.totalPrice || subtotal + taxValue
    const discount = displayOrderDetails.discount || 
                     displayOrderDetails.applied_discount || 
                     (displayOrderDetails as ExtendedOrderDetails).coupon_discount || 
                     (displayOrderDetails as ExtendedOrderDetails).promo_discount || 0
    const tablePrice = displayOrderDetails.table_price || displayOrderDetails.tablePrice || "0"
    const deliveryPrice = displayOrderDetails.delivery_price || displayOrderDetails.deliveryPrice || 0
    
    // تحويل الخصم إلى رقم إذا كان string
    const parsedDiscount = typeof discount === 'string' ? parseFloat(discount) : discount
    const safeDiscount = isNaN(parsedDiscount) ? 0 : parsedDiscount
    
    // التحقق من صحة القيم قبل التحويل
    const parsedTablePrice = parseFloat(tablePrice)
    const safeTablePrice = isNaN(parsedTablePrice) ? 0 : parsedTablePrice
    
    return {
      subtotal,
      taxValue,
      totalPrice,
      discount: safeDiscount,
      tablePrice: safeTablePrice,
      deliveryPrice,
    }
  }, [displayOrderDetails])

  const finalDisplayOrderDetails: ExtendedOrderDetails | null = displayOrderDetails || (actualOrderId ? {
    id: actualOrderId,
    order_number: actualOrderId,
    payment_status: 'paid',
    status: 'confirmed',
    created_at: new Date().toISOString(),
    customer_phone: t('successScreen.loadingText'),
    payment_method: "online",
    user: {
      name: t('successScreen.loadingText'),
      phone: t('successScreen.loadingText')
    },
    branch: t('successScreen.loadingText'),
    items: [],
    total_price: 0,
    subtotal: 0,
    tax_value: 0,
    discount: 0,
    promotion: null,
    table: undefined,
    tax_percentage: undefined,
    price_before_discount: undefined,
    price: undefined,
    applied_discount: undefined,
    taxValue: undefined,
    totalPrice: undefined,
    table_price: undefined,
    tablePrice: undefined,
    delivery_price: undefined,
    deliveryPrice: undefined
  } : null)

  // حساب الإجماليات
  const finalOrderCalculations = useMemo(() => {
    if (!finalDisplayOrderDetails) return null
    
    const subtotal = finalDisplayOrderDetails.subtotal || finalDisplayOrderDetails.price_before_discount || finalDisplayOrderDetails.price || 0
    const taxValue = finalDisplayOrderDetails.tax_value || finalDisplayOrderDetails.taxValue || 0
    const totalPrice = finalDisplayOrderDetails.total_price || finalDisplayOrderDetails.totalPrice || subtotal + taxValue
    const discount = finalDisplayOrderDetails.discount || 
                     finalDisplayOrderDetails.applied_discount || 
                     (finalDisplayOrderDetails as ExtendedOrderDetails).coupon_discount || 
                     (finalDisplayOrderDetails as ExtendedOrderDetails).promo_discount || 0
    
    // تحويل الخصم إلى رقم إذا كان string
    const parsedDiscount = typeof discount === 'string' ? parseFloat(discount) : discount
    const safeDiscount = isNaN(parsedDiscount) ? 0 : parsedDiscount
    
    // لوغ لتتبع قيم الخصم
    if (finalDisplayOrderDetails.discount || finalDisplayOrderDetails.applied_discount || 
        (finalDisplayOrderDetails as ExtendedOrderDetails).coupon_discount || 
        (finalDisplayOrderDetails as ExtendedOrderDetails).promo_discount) {
      console.log('Discount values found:', {
        discount: finalDisplayOrderDetails.discount,
        applied_discount: finalDisplayOrderDetails.applied_discount,
        coupon_discount: (finalDisplayOrderDetails as ExtendedOrderDetails).coupon_discount,
        promo_discount: (finalDisplayOrderDetails as ExtendedOrderDetails).promo_discount,
        final_discount: discount
      })
    }
    const tablePrice = finalDisplayOrderDetails.table_price || finalDisplayOrderDetails.tablePrice || "0"
    const deliveryPrice = finalDisplayOrderDetails.delivery_price || finalDisplayOrderDetails.deliveryPrice || 0
    
    // التحقق من صحة القيم قبل التحويل
    const parsedTablePrice = parseFloat(tablePrice)
    const safeTablePrice = isNaN(parsedTablePrice) ? 0 : parsedTablePrice
    
    return {
      subtotal,
      taxValue,
      totalPrice,
      discount: safeDiscount,
      tablePrice: safeTablePrice,
      deliveryPrice,
    }
  }, [finalDisplayOrderDetails])

  const orderPromotionDisplay = useMemo(
    () => fromOrderPromotion(finalDisplayOrderDetails?.promotion),
    [finalDisplayOrderDetails?.promotion],
  )

  const promotionSummaryRows = useMemo(
    () => offerSummaryRows(orderPromotionDisplay),
    [orderPromotionDisplay],
  )

  const hasPromotionDiscountRow = promotionSummaryRows.some(
    (row) => row.kind === 'discount' || row.kind === 'set_item',
  )

  // فقط إذا لم يكن لدينا orderId على الإطلاق ولا توجد أي بيانات محفوظة
  // ولم نكن في عملية تحميل، اعرض رسالة خطأ
  if (!actualOrderId && !finalDisplayOrderDetails && !isRetrying && !orderDetails) {
    // تحقق من وجود بيانات محفوظة قبل عرض رسالة الخطأ
    const storedOrderData = sessionStorage.getItem('currentOrderData') || 
                           sessionStorage.getItem('orderData') ||
                           sessionStorage.getItem('currentOrderCartData') ||
                           sessionStorage.getItem('orderCartData')
    
    if (storedOrderData) {
      // إذا وجدت بيانات محفوظة، لا تعرض رسالة الخطأ
      return null
    }
    
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-[#E6F4EA] to-[#C8E6C9]">
        <div className="bg-white rounded-xl shadow-md p-6 max-w-sm w-full text-center">
          <div className="mb-4">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-2 font-medium">
              {t('successScreen.orderDetailsNotFound')}
            </p>
          </div>
          <div className="space-y-2">
            <button
              onClick={handleBackToHome}
              className="w-full py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t('successScreen.backToHome')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // اعرض الصفحة دائماً مع البيانات المتوفرة
  const isDataLoading = !displayOrderDetails && actualOrderId

  // التأكد من وجود البيانات النهائية - إذا لم تكن متوفرة، اعرض شاشة تحميل بدلاً من null
  if (!finalDisplayOrderDetails) {
    // إذا كان لدينا orderId ونحن في عملية تحميل، اعرض شاشة تحميل
    if (actualOrderId && (isRetrying || isDataLoading)) {
      return (
        <div className="min-h-screen grid place-items-center bg-gradient-to-b from-[#E6F4EA] to-[#C8E6C9]">
          <div className="bg-white rounded-xl shadow-md p-6 max-w-sm w-full text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
              <p className="text-gray-600 mb-2 font-medium">
                {t('successScreen.loadingOrderDetails')}
              </p>
              <p className="text-gray-500 text-sm">
                {t('successScreen.pleaseWait')}
              </p>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div className="min-h-screen relative bg-gradient-to-b from-[#E6F4EA] to-[#C8E6C9]" >
      <div className="flex items-start justify-start text-right px-4 pt-3">
   <button
        onClick={handleClose}
        className="  w-10 h-10 rounded-lg bg-white shadow flex items-center justify-center"
      >
        <X className="w-5 h-5" />
      </button>
  
  </div>     

      {/* مؤشر تحديث البيانات في الخلفية */}
      {(isRetrying || isDataLoading) && (
        <div className="absolute top-16 left-4 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2 z-10">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-xs text-blue-700">
            {isRetrying ? t('successScreen.updatingData') : t('successScreen.loadingOrderDetails')}
          </span>
        </div>
      )}

      <div className="w-full mx-auto pt-0 px-4 pb-4 text-center">
        <div className="w-24 h-24 place-content-center mx-auto">
          <img src="/done.png" alt="Success" className="w-22 h-22 mx-auto mb-0" />
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 mt-4 mb-4">{t('successScreen.orderSentSuccessfully')}</h1>

        <div className="bg-white rounded-xl shadow-md p-5 text-right">
          <div className="text-xs text-gray-500 text-center mb-2">
            {fmtDate(finalDisplayOrderDetails.created_at || new Date().toISOString())}
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg py-2 text-center mb-4">
            <p className="text-xs text-gray-500">{t('order.orderNumber')}</p>
            <p className="text-2xl font-bold text-gray-600">
              {finalDisplayOrderDetails.order_number || finalDisplayOrderDetails.id}
            </p>
          </div>

          {/* ------------------- بيانات العميل ------------------- */}
          <div className="space-y-2 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('successScreen.customerName')}</span>
              <span className={isDataLoading ? "animate-pulse bg-gray-200 rounded px-2" : ""}>
                {finalDisplayOrderDetails.user?.name || t('successScreen.notAvailable')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('successScreen.customerPhone')}</span>
              <span className={isDataLoading ? "animate-pulse bg-gray-200 rounded px-2" : ""}>
                {finalDisplayOrderDetails.customer_phone || finalDisplayOrderDetails.user?.phone || t('successScreen.notAvailable')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('successScreen.branch')}</span>
              <span className={isDataLoading ? "animate-pulse bg-gray-200 rounded px-2" : ""}>
                {typeof finalDisplayOrderDetails.branch === 'string' 
                  ? finalDisplayOrderDetails.branch 
                  : finalDisplayOrderDetails.branch?.name || t('successScreen.notAvailable')
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('successScreen.paymentMethod')}</span>
              <span>{finalDisplayOrderDetails.payment_method || t('successScreen.online')}</span>
            </div>
          </div>
              
          {/* ------------------- تفاصيل الطاولة ------------------- */}
          {finalDisplayOrderDetails.table && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <h3 className="font-semibold mb-2 text-gray-700">{t('successScreen.tableDetails')}</h3>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('successScreen.tableNumber')}</span>
                <span className="font-medium text-gray-800">{finalDisplayOrderDetails.table?.name || t('successScreen.notAvailable')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('successScreen.tablePrice')}</span>
                <span className="font-medium text-gray-800">{sarWithSeparatedFractions(finalOrderCalculations?.tablePrice || 0)}</span>
              </div>
            </div>
          )}

          {/* ------------------- جدول المنتجات ------------------- */}
          <div className="pt-1 mt-1">
            <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
              <span className={`w-1/3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('successScreen.products')}</span>
              <span className="w-1/3 text-center">{t('successScreen.quantity')}</span>
              <span className={`w-1/3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('successScreen.price')}</span>
            </div>

            {finalDisplayOrderDetails.items && finalDisplayOrderDetails.items.length > 0 ? (
              finalDisplayOrderDetails.items.map((item: any, i: number) => (
              <div key={i} className="py-2">
                {/* اسم المنتج والكمية والسعر */}
                <div className="flex justify-between items-start">
                  <span className={`w-1/3 ${isRTL ? 'text-right' : 'text-left'} text-gray-800 font-medium`}>
                  {item.meals?.[0]?.name || item.name || t('successScreen.product')}
                  </span>
                  <span className="w-1/3 text-center font-medium">{item.quantity === null || item.quantity === undefined ? 1 : (item.quantity || 1)}</span>
                  <span className={`w-1/3 ${isRTL ? 'text-left' : 'text-right'} text-gray-600 font-medium`}>
                    {sarWithSeparatedFractions(calculateItemTotalPrice(item))}
                  </span>
                </div>
                
                {/* الإضافات */}
                  {item.meals?.[0]?.additionals?.length > 0 && (
                  <div className={`mt-1 ${isRTL ? 'mr-4' : 'ml-4'}`}>
                    <div className={`text-xs text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                      - {item.meals[0].additionals.map((addon: any) => addon.name).join(' - ')}
                    </div>
                    </div>
                  )}
                  </div>
              ))
            ) : (
               <div className="py-4 text-center">
                <p className="text-gray-500 text-sm">{t('successScreen.loadingProductDetails')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('successScreen.detailsWillAppear')}</p>
              </div>
            )}
          </div>
          
          {/* خط فاصل تحت المنتجات */}
          <hr className="border-gray-300 mt-2" />

          {orderPromotionDisplay.offers.length > 0 ||
          orderPromotionDisplay.freeItems.length > 0 ? (
            <div className="mt-3 mb-2">
              <CheckoutOfferCard display={orderPromotionDisplay} />
            </div>
          ) : null}

          {/* ------------------- الإجماليات ------------------- */}
          <div className="mt-2 pt-1 text-sm space-y-1">
            <div className="flex justify-between">
              <span>{t('successScreen.subtotal')}</span>
              <span className={`text-gray-600 ${isDataLoading ? "animate-pulse bg-gray-200 rounded px-2" : ""}`}>
                {isDataLoading ? t('successScreen.loadingText') : sarWithSeparatedFractions(finalOrderCalculations?.subtotal || 0)}
              </span>
            </div>
            {(finalOrderCalculations?.tablePrice || 0) > 0 && (
              <div className="flex justify-between">
                <span>{t('successScreen.tablePrice')}</span>
                <span className="text-gray-600">{sarWithSeparatedFractions(finalOrderCalculations?.tablePrice || 0)}</span>
                </div>
              )}
            {(finalOrderCalculations?.deliveryPrice || 0) > 0 && (
              <div className="flex justify-between">
                <span>{t('successScreen.deliveryFees')}</span>
                <span className="text-gray-600">{sarWithSeparatedFractions(finalOrderCalculations?.deliveryPrice || 0)}</span>
              </div>
            )}
            {promotionSummaryRows.map((row, index) => (
              <div
                key={`promo-row-${index}`}
                className={`flex justify-between ${row.showMinus ? 'text-green-600' : 'text-gray-600'}`}
              >
                <span>{receiptPromotionLabel(row, t)}</span>
                <span>
                  {row.showMinus ? '-' : ''}
                  {sarWithSeparatedFractions(row.amount ?? 0)}
                </span>
              </div>
            ))}
            {!hasPromotionDiscountRow && (finalOrderCalculations?.discount || 0) > 0.01 && (
              <div className="flex justify-between text-green-600">
                <span>{t('successScreen.couponDiscount')}</span>
                <span>-{sarWithSeparatedFractions(finalOrderCalculations?.discount || 0)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>{t('successScreen.tax')} {finalDisplayOrderDetails.tax_percentage}</span>
              <span className={`text-gray-600 ${isDataLoading ? "animate-pulse bg-gray-200 rounded px-2" : ""}`}>
                {isDataLoading ? t('successScreen.loadingText') : sarWithSeparatedFractions(finalOrderCalculations?.taxValue || 0)}
              </span>
            </div>
            <p className="text-center text-gray-400 mt-1">----------------------------------------------------------------------------------------------------------------------------------------</p>
            <div className="flex justify-between font-bold pt-1">
              <span>{t('successScreen.grandTotal')}</span>
              <span className={`text-gray-700 ${isDataLoading ? "animate-pulse bg-gray-200 rounded px-2" : ""}`}>
                {isDataLoading ? t('successScreen.loadingText') : sarWithSeparatedFractions(finalOrderCalculations?.totalPrice || 0)}
              </span>
            </div>
          </div>

          <p className="text-[11px] text-gray-500 mt-2 text-start">
            {t('successScreen.invoiceNote')} <br />
            {t('successScreen.depositRefundNote')}
          </p>
        </div>
      </div>

      <div className="px-4 pb-4">
      <button
            onClick={handleDownloadInvoice}
            className={`w-full h-12 rounded-lg text-white font-bold flex items-center justify-center gap-2 ${isDataLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{backgroundColor: primaryColor}}
            disabled={!!isDataLoading}
          >
          {isDataLoading ? t('successScreen.loadingDataForInvoice') : t('successScreen.downloadInvoice')}
          </button>
      </div>
    </div>
  )
}
  
